/**
 * The UI/UX pack's GUI (Figma's), what the `uiux` plugin's data cannot say: its elements drawn
 * on the board (`elements.tsx`: button, input, checkbox, list, instance), frames that are
 * screens made by a click at a phone's size, the style bar's screen, element and prototype
 * tools (`stylebar/`), prototype mode (`Interactions.tsx`: where each click leads, set by
 * dragging a handle onto a screen), components and instances (`components.ts`), the Prototype
 * window that plays it and its export as a site (`prototype/`, `PrototypeWindow.tsx`), and its
 * service (`service.ts`), which its own windows reach with `usePack("uiux")`.
 *
 * It takes part where the environment worked in lists the Prototype window (UI/UX does). Its
 * kinds and their tools' keys are the plugin's (`plugins/uiux`, which also names this pack's window
 * and commands for the config), its commands' keys are here, its looks `uiux.css` and `prototype.css`.
 */
import type { App } from "../../app/context";
import { toast } from "../../shared/toast";
import type { GuiPack } from "../../state/packs";
import { DEVICES, FIRST_DEVICE, SCREEN_FILL } from "./constants";
import { UI_ELEMENTS } from "./elements";
import { Interactions } from "./Interactions";
import { FrameAfter, FrameBefore, PlayButton, PrototypeIcon } from "./parts";
import { createPrototype } from "./prototype";
import { PrototypeWindow } from "./PrototypeWindow";
import { screenAt } from "./screens";
import { createUiux, PROTOTYPE_MODE, type UiUx } from "./service";
import { PrototypeTools } from "./stylebar/PrototypeTools";
import { ElementTools, ScreenTools } from "./stylebar/ScreenTools";
import "./uiux.css";
import "./prototype.css";

/** The window UI/UX lists, which marks the environments where the pack takes part. */
const PROTOTYPE_WINDOW = "prototype";

/** The UI/UX pack. */
export const uiux: GuiPack<App, UiUx> = {
  id: "uiux",
  summary: "UI/UX's elements, screens, components, prototype mode and the Prototype window",
  activeIn: (environment) => !!environment?.windows.includes(PROTOTYPE_WINDOW),
  elements: UI_ELEMENTS,
  frames: {
    tool: "Screen (frame)",
    here: "Screen here",
    name: "Screen",
    clicked: { w: DEVICES[FIRST_DEVICE]!.w, h: DEVICES[FIRST_DEVICE]!.h, style: { fill: SCREEN_FILL, device: FIRST_DEVICE } },
    drawn: { fill: SCREEN_FILL },
  },
  windows: [{ id: PROTOTYPE_WINDOW, title: "Prototype", group: "Graph", component: PrototypeWindow }],
  keys: [
    { keys: "Shift+E", action: "board.prototype", argument: null },
    { keys: "Shift+P", action: "prototype.play", argument: null },
  ],
  needs: ["graph", "actions", "kinds", "view", "board", "project", "assets", "wm"],
  start({ graph, actions, kinds, view, board, project, assets, wm }, context) {
    const service = createUiux(
      {
        graph,
        actions,
        kinds,
        view,
        board,
        prototype: ({ active }) => createPrototype({ graph, assets, active, kinds, title: () => project.state()?.name ?? "" }),
      },
      context,
    );
    /** Runs `then` only while the board is shown. */
    const onBoard = (then: () => unknown) => () => void (view.mode() === "board" && then());
    return {
      service,
      modes: [
        {
          id: PROTOTYPE_MODE,
          title: "Prototype: where each click leads",
          command: "board.prototype",
          Icon: PrototypeIcon,
          Overlay: Interactions,
          drop: (from, at) => service.lead(from, screenAt(board.content(), at) ?? ""),
          itemClass: (node) => ({ leads: !!node.style?.navigate }),
        },
      ],
      sections: [
        { id: "screen", when: (bar) => bar.shape() === "frame", Tools: ScreenTools },
        { id: "element", when: () => true, Tools: ElementTools },
        { id: "prototype", mode: PROTOTYPE_MODE, when: () => true, Tools: PrototypeTools },
      ],
      frames: { classes: (frame) => ({ component: frame.group.style?.component === "yes", screen: frame.depth === 0 }), Before: FrameBefore, After: FrameAfter },
      boardMenu(_at, canEdit) {
        const selected = board.selected();
        const frame = selected.length === 1 ? board.content().frames.find((f) => f.id === selected[0]) : undefined;
        if (!canEdit || !frame) return [];
        const component = frame.group.style?.component === "yes";
        const screen = service.screens().some((s) => s.id === frame.id);
        return [
          ...(component ? [{ label: "Make an instance", action: () => void service.instance(frame.id) }] : []),
          { label: component ? "Stop being a component" : "Make it a component", action: () => void service.toggleComponent() },
          ...(screen ? [{ label: "Start the prototype here", action: () => void service.makeStart() }, { label: "Play from this screen", command: "prototype.play" }] : []),
        ];
      },
      Tools: PlayButton,
      hint: "Draw a screen with Shift+F (a click: a phone), put buttons (B), inputs (Shift+I) and text (T) on it, then in prototype mode (Shift+E) drag from a button to another screen. Shift+P plays it.",
      commands: {
        "board.prototype": onBoard(() => (service.active() ? service.setPrototyping(!service.prototyping()) : toast("Prototype mode is UI/UX's: its frames are screens, and a click may lead from one to another."))),
        "prototype.play": () => {
          if (!service.active()) return void toast("The prototype is UI/UX's: turn on the UI/UX pack and work in it.");
          const chosen = board.selected().find((id) => service.screens().some((s) => s.id === id));
          service.prototype.setStart(chosen ?? null);
          service.prototype.restart();
          wm.open(PROTOTYPE_WINDOW);
        },
      },
      menus: [
        {
          menu: "view",
          section: "Prototype",
          order: 45,
          entries: () =>
            service.active() && view.mode() === "board"
              ? [
                  { label: "Prototype mode", command: "board.prototype" },
                  { label: "Play the prototype", command: "prototype.play" },
                  {
                    label: "Export the prototype as a site…",
                    action: () => void service.prototype.exportSite().then((ok) => toast(ok ? "The prototype was saved as one page that plays by itself." : "No screen yet: draw a frame (Shift+F) first.")),
                  },
                ]
              : [],
        },
      ],
    };
  },
};
